import React, { MouseEventHandler } from 'react';
import { ProductOutput } from 'graphql/generated/hasura';
import { ReactComponent as DefaultTest } from 'assets/icons/testKits/testKit.svg';
import ButtonComponent from 'components/button/buttonComponent';
import ImageMiniComponent from 'components/ImageMiniComponent';
import { useGetFileFromStorage } from 'hooks/useGetFileFromStorage';

interface TestBlockProps extends Omit<ProductOutput, 'is_shipmonk_deleted'> {
  isLoadingCartMutation?: boolean;
  isLoadingCartData?: boolean;
  hasSelectedProduct: boolean;
  addToCart: MouseEventHandler<HTMLButtonElement>;
  removeFromCart: MouseEventHandler<HTMLButtonElement>;
  viewDetail: () => void;
  hideButtons?: boolean;
}

const TestBlock: React.FC<TestBlockProps> = ({
  image_url,
  id,
  title,
  description,
  addToCart,
  removeFromCart,
  hideButtons = false,
  viewDetail,
  hasSelectedProduct,
  isLoadingCartData,
  isLoadingCartMutation,
  default_image_id,
  price,
}) => {
  const { getUrlFileFromProductsStorage } = useGetFileFromStorage();

  const reschedule = async (mediaId?: string) => {
    if (!mediaId) {
      return;
    }
    const url = await getUrlFileFromProductsStorage(mediaId);
    return url;
  };

  const showFallback = !image_url;

  return (
    <div className="flex flex-col w-full border border-gray-opacity-15 rounded-10 overflow-hidden">
      <div className="w-full h-[280px]">
        {showFallback ? (
          <div className="flex items-center justify-center w-full h-full bg-white">
            <DefaultTest className="h-10 w-10 fill-charcoal-gray stroke-charcoal-gray" />
          </div>
        ) : (
          <ImageMiniComponent
            altText={'mediaAlt'}
            initialMediaUrl={image_url}
            reschedule={reschedule}
            mediaId={default_image_id ?? ''}
            key={image_url}
            maxRetry={2}
            className={
              'bg-frost-blue text-med-gray w-full max-h-[278px] h-full'
            }
          />
        )}
      </div>
      <div
        onClick={viewDetail}
        className="flex h-full cursor-pointer max-h-[176px] flex-col gap-2.5 bg-base-content group hover:bg-hover-blue z-10 p-4"
      >
        <p className="group-hover:text-clc-blue group-hover:underline text-neutral-800 text-base font-semibold">
          {title}
        </p>
        <p className="text-sm text-med-gray-3 font-medium line-clamp-2">
          {description}
        </p>
        <div className="flex flex-row justify-between h-[41px] mt-auto">
          <span className="text-h3 text-neutral-800 font-semibold">
            ${price}
          </span>
          {!hideButtons && (
            <>
              {hasSelectedProduct ? (
                <ButtonComponent
                  type="underline"
                  iconPosition="left"
                  disabled={isLoadingCartData}
                  showSpinner={isLoadingCartData}
                  onClick={removeFromCart}
                  testID={`selected-${id}`}
                >
                  Added to Cart
                </ButtonComponent>
              ) : (
                <ButtonComponent
                  className="w-fit !text-sm"
                  testID={`add-to-cart-${id}`}
                  disabled={
                    isLoadingCartMutation ||
                    hasSelectedProduct ||
                    isLoadingCartData
                  }
                  onClick={addToCart}
                  showSpinner={isLoadingCartMutation || isLoadingCartData}
                >
                  Add to cart
                </ButtonComponent>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestBlock;
